import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { getSupabase } from "@/lib/supabase/client";

export type ApiError = {
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

export class ApiException extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiException";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const baseURL =
  (process.env.NEXT_PUBLIC_API_URL && process.env.NEXT_PUBLIC_API_URL.trim()) ||
  "http://localhost:8000";

if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.info("[cms] API baseURL =", baseURL);
}

/**
 * Shared axios instance for every CMS API call.
 *
 * - JWT'i Supabase session'ından otomatik yapıştırır
 * - 401'de bir kez refresh + retry, başarısız olursa /login'e yönlendirir
 * - Backend'in `{error: {code, message, details}}` zarfını `ApiException`
 *   olarak fırlatır
 * - Timeout 60s (Railway cold-start için), uzun analytics sorgularını
 *   bireysel call'lar override edebilir.
 */
export const apiClient = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
  timeout: 60_000,
});

type RetryableConfig = InternalAxiosRequestConfig & { _retry401?: boolean };

apiClient.interceptors.request.use(async (config) => {
  try {
    const supabase = getSupabase();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.set("Authorization", `Bearer ${session.access_token}`);
    }
  } catch {
    // No supabase env yet — request goes out unauthenticated.
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<{ error?: ApiError }>) => {
    const status = error.response?.status ?? 0;
    const config = error.config as RetryableConfig | undefined;

    if (status === 401 && config && !config._retry401) {
      config._retry401 = true;
      try {
        const supabase = getSupabase();
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && data?.session?.access_token) {
          config.headers = config.headers ?? {};
          (config.headers as Record<string, string>).Authorization =
            `Bearer ${data.session.access_token}`;
          return apiClient.request(config as AxiosRequestConfig);
        }
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          const redirect = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.assign(`/login?redirect=${redirect}`);
        }
      } catch {
        /* fall through to error path */
      }
    }

    const payload = error.response?.data?.error;
    if (payload) {
      throw new ApiException(payload.code, payload.message, status, payload.details ?? {});
    }
    const axiosCode = (error as { code?: string }).code;
    if (axiosCode === "ECONNABORTED" || axiosCode === "ETIMEDOUT") {
      throw new ApiException(
        "timeout",
        "İşlem beklenenden uzun sürdü. Sunucu hâlâ çalışıyor olabilir.",
        status,
        {},
      );
    }
    throw new ApiException(
      "network_error",
      "Sunucuya ulaşılamadı. İnternet bağlantını veya backend durumunu kontrol et.",
      status,
      {},
    );
  },
);

/**
 * CMS error renderer — kısa, operatör diline yakın Türkçe mesajlar.
 * Frontend'in formatApiError'undan biraz daha "kuru/teknik" — admin
 * panel kullanıcısı hangi alanda ne kırıldığını teknik düzeyde bilebilir.
 */
export function formatApiError(err: unknown): string {
  if (err instanceof ApiException) {
    switch (err.code) {
      case "unauthorized":
        return "Oturumun sona erdi, tekrar giriş yap.";
      case "forbidden":
        return "Bu işlem için super-admin yetkisi gerekli.";
      case "not_found":
        return err.message || "Kayıt bulunamadı.";
      case "validation_error":
        return err.message || "Bilgilerde eksik veya hatalı alan var.";
      case "conflict":
        return err.message || "Bu işlem mevcut durumla çakışıyor.";
      case "cost_limit_exceeded":
        return "Sistem günlük AI bütçesini aştı.";
      case "external_service_error":
      case "external_service_timeout":
        return "Bir dış servise ulaşamadık.";
      case "timeout":
        return err.message || "İşlem zaman aşımına uğradı.";
      case "network_error":
        return err.message || "Bağlantı kurulamadı.";
      default:
        return err.message || `Hata (${err.code}).`;
    }
  }
  if (err instanceof Error) return err.message || "Bilinmeyen hata.";
  return "Bilinmeyen hata.";
}
